import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Lightbulb, TrendingUp, Clock, AlertTriangle, BarChart3 } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { toast } from "sonner";

const TYPE_LABEL = {
  auto: "Auto", moto: "Moto", casa: "Casa", vita: "Vita", salute: "Salute",
  infortuni: "Infortuni", viaggio: "Viaggio", responsabilita_civile: "Resp. civile",
};

export default function BusinessInsights() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/insights/business")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-8 text-slate-500">Caricamento insight…</div>;
  if (!data) return null;

  return (
    <div className="space-y-6" data-testid="business-insights-page">
      <header>
        <p className="text-xs uppercase tracking-wider text-slate-500">Gestione Agenzia</p>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight flex items-center gap-2">
          <Lightbulb className="h-7 w-7 text-amber-500" /> Insight commerciali
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Statistiche aggregate sulle azioni operative per identificare opportunità e colli di bottiglia.
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top template usati */}
        <section className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <BarChart3 className="h-4 w-4 text-blue-600" />
            <h2 className="font-semibold text-sm text-slate-800">Top template usati</h2>
          </div>
          {data.top_templates.length === 0 ? (
            <p className="text-xs text-slate-400 italic">Nessun template ancora utilizzato</p>
          ) : (
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.top_templates} layout="vertical" margin={{ left: 4, right: 16 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis type="number" tick={{ fontSize: 11, fill: "#64748B" }} allowDecimals={false} />
                  <YAxis type="category" dataKey="title" tick={{ fontSize: 10, fill: "#64748B" }} width={160} />
                  <Tooltip contentStyle={{ fontSize: 12 }} />
                  <Bar dataKey="count" name="Usi" fill="#3B82F6" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </section>

        {/* Conversione offerte per tipo */}
        <section className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="h-4 w-4 text-emerald-600" />
            <h2 className="font-semibold text-sm text-slate-800">Conversione offerte per tipo polizza</h2>
          </div>
          {data.conversion_by_type.length === 0 ? (
            <p className="text-xs text-slate-400 italic">Nessuna offerta registrata</p>
          ) : (
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase tracking-wide text-slate-500">
                <tr><th className="text-left py-1">Tipo</th><th className="text-right">Totali</th><th className="text-right">Pagate</th><th className="text-right">Tasso</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.conversion_by_type.map((c) => (
                  <tr key={c.type}>
                    <td className="py-2 font-medium">{TYPE_LABEL[c.type] || c.type}</td>
                    <td className="text-right tabular-nums">{c.total}</td>
                    <td className="text-right tabular-nums text-emerald-700 font-semibold">{c.paid}</td>
                    <td className="text-right">
                      <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                        c.conversion_rate >= 70 ? "bg-emerald-100 text-emerald-800" :
                        c.conversion_rate >= 40 ? "bg-amber-100 text-amber-800" :
                        "bg-rose-100 text-rose-800"
                      }`}>{c.conversion_rate}%</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        {/* Tempo medio risposta partner */}
        <section className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Clock className="h-4 w-4 text-blue-600" />
            <h2 className="font-semibold text-sm text-slate-800">Tempo medio risposta partner</h2>
          </div>
          {data.avg_response_by_partner.length === 0 ? (
            <p className="text-xs text-slate-400 italic">Nessuna risposta registrata</p>
          ) : (
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase tracking-wide text-slate-500">
                <tr><th className="text-left py-1">Partner</th><th className="text-right">Risposte</th><th className="text-right">Tempo medio</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.avg_response_by_partner.map((p, i) => (
                  <tr key={i}>
                    <td className="py-2 font-medium">{p.partner}</td>
                    <td className="text-right tabular-nums text-slate-600">{p.responses}</td>
                    <td className="text-right tabular-nums">
                      <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                        p.avg_hours < 24 ? "bg-emerald-100 text-emerald-800" :
                        p.avg_hours < 72 ? "bg-amber-100 text-amber-800" :
                        "bg-rose-100 text-rose-800"
                      }`}>
                        {p.avg_hours < 24 ? `${p.avg_hours}h` : `${p.avg_days}g`}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        {/* Colli di bottiglia */}
        <section className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="h-4 w-4 text-rose-600" />
            <h2 className="font-semibold text-sm text-slate-800">Colli di bottiglia (oltre 5 giorni senza risposta)</h2>
          </div>
          {data.bottlenecks.length === 0 ? (
            <p className="text-xs text-emerald-700 italic font-medium">✓ Nessun collo di bottiglia — tutti i partner rispondono entro 5 giorni</p>
          ) : (
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase tracking-wide text-slate-500">
                <tr><th className="text-left py-1">Partner</th><th className="text-right">In ritardo</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.bottlenecks.map((b, i) => (
                  <tr key={i}>
                    <td className="py-2 font-medium">{b.partner}</td>
                    <td className="text-right">
                      <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-bold bg-rose-100 text-rose-800">{b.stale_count} ⚠️</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </div>
    </div>
  );
}
